import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { PolicyDiff, PolicyRule } from '../../api/types';
import { vt } from '../../theme';
import { formatEndpoint, formatPorts, formatProtocol } from './model';

const useStyles = makeStyles({
  group: { marginBottom: '14px' },
  groupTitle: { margin: '0 0 6px', fontSize: '13px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  list: { margin: 0, paddingInlineStart: '18px', display: 'flex', flexDirection: 'column', rowGap: '4px' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11.5px' },
  before: { color: vt.red, textDecorationLine: 'line-through' },
  after: { color: vt.green },
  empty: { color: vt.fg3 },
});

/** One line per rule: "allow TCP tag:laptop → tag:workstation 3389 · RDP from laptops". */
export function ruleSummary(r: PolicyRule): string {
  const ports = r.destinationPorts === 'any' ? '' : ` ${formatPorts(r.destinationPorts)}`;
  const comment = r.comment ? ` · ${r.comment}` : '';
  const off = r.enabled ? '' : ' (off)';
  return `${r.action} ${formatProtocol(r.protocol)} ${formatEndpoint(r.source)} → ${formatEndpoint(r.destination)}${ports}${comment}${off}`;
}

const show = (v: unknown) => (typeof v === 'boolean' ? (v ? 'on' : 'off') : String(v));

/** Active version → draft, the way the contract's PolicyDiff groups it. */
export function DiffDialog({ diff, open, onClose }: { diff: PolicyDiff | null; open: boolean; onClose: () => void }) {
  const s = useStyles();
  return (
    <Dialog open={open} onOpenChange={(_e, data) => !data.open && onClose()}>
      <DialogSurface aria-label="Review diff">
        <DialogBody>
          <DialogTitle>
            {diff ? `v${diff.fromVersion} → draft r${diff.toRevision} · ${diff.changeCount} change${diff.changeCount === 1 ? '' : 's'}` : 'Diff'}
          </DialogTitle>
          <DialogContent>
            {diff && diff.changeCount === 0 && <p className={s.empty}>The draft matches the active version.</p>}
            {diff && diff.added.length > 0 && (
              <section className={s.group} aria-label="Added">
                <h3 className={s.groupTitle}>Added</h3>
                <ul className={s.list}>
                  {diff.added.map((r) => (
                    <li key={r.id} className={s.mono}>
                      {ruleSummary(r)}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {diff && diff.removed.length > 0 && (
              <section className={s.group} aria-label="Removed">
                <h3 className={s.groupTitle}>Removed</h3>
                <ul className={s.list}>
                  {diff.removed.map((r) => (
                    <li key={r.id} className={s.mono}>
                      {ruleSummary(r)}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {diff && diff.changed.length > 0 && (
              <section className={s.group} aria-label="Changed">
                <h3 className={s.groupTitle}>Changed</h3>
                <ul className={s.list}>
                  {diff.changed.map((c) => (
                    <li key={c.id}>
                      <span className={s.mono}>{c.after.comment || c.id}</span>
                      <ul className={s.list}>
                        {c.fields.map((f) => (
                          <li key={f} className={s.mono} data-field={f}>
                            {f}: <span className={s.before}>{show(c.before[f])}</span> →{' '}
                            <span className={s.after}>{show(c.after[f])}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {diff && diff.moved.length > 0 && (
              <section className={s.group} aria-label="Moved">
                <h3 className={s.groupTitle}>Moved</h3>
                <ul className={s.list}>
                  {diff.moved.map((m) => (
                    <li key={m.id} className={s.mono}>
                      #{m.from} → #{m.to}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
